"use client";

import { useIMQA } from "@/components/imqa-provider";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState, useCallback, useRef } from "react";

// JSON 구문 하이라이트 컴포넌트
function JsonHighlight({ data }: { data: Record<string, unknown> }) {
  const syntaxHighlight = useCallback((json: string) => {
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'text-orange-500'; // number
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'text-blue-600 dark:text-blue-400'; // key
          } else {
            cls = 'text-green-600 dark:text-green-400'; // string
          }
        } else if (/true|false/.test(match)) {
          cls = 'text-purple-600 dark:text-purple-400'; // boolean
        } else if (/null/.test(match)) {
          cls = 'text-gray-500'; // null
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }, []);

  const formattedJson = JSON.stringify(data, null, 2);
  const highlightedJson = syntaxHighlight(formattedJson);

  return (
    <pre
      className="text-xs font-mono bg-gray-50 dark:bg-gray-900 p-3 rounded-lg overflow-x-auto max-h-64 border whitespace-pre-wrap break-words"
      dangerouslySetInnerHTML={{ __html: highlightedJson }}
    />
  );
}

// Web Vitals 타입
interface WebVitals {
  LCP?: number;
  FCP?: number;
  CLS?: number;
  FID?: number;
  TTFB?: number;
  INP?: number;
}

export function IMQAStatus() {
  const { isInitialized, config } = useIMQA();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [IMQA, setIMQA] = useState<any>(null);
  const [resourceAttributes, setResourceAttributes] = useState<Record<string, unknown> | null>(null);
  const [showAttributes, setShowAttributes] = useState(false);
  const [webVitals, setWebVitals] = useState<WebVitals>({});
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // resourceAttributes 업데이트 함수
  const updateResourceAttributes = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).IMQA?.resourceAttributes) {
      const attrs = (window as any).IMQA.resourceAttributes;
      setResourceAttributes(attrs);
      setLastUpdated(new Date());
    }
  }, []);

  useEffect(() => {
    const importIMQA = async () => {
      try {
        const IMQAModule = await import('@imqa/web-agent');
        setIMQA(IMQAModule.default);
      } catch (error) {
        console.error('Failed to import IMQA:', error);
      }
    };

    importIMQA();
  }, []);

  // Web Vitals 관찰
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // PerformanceObserver로 Web Vitals 감시
    const observers: PerformanceObserver[] = [];

    try {
      // LCP 관찰
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
        if (lastEntry) {
          setWebVitals(prev => ({ ...prev, LCP: Math.round(lastEntry.startTime) }));
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);

      // FCP 관찰
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries.find(e => e.name === 'first-contentful-paint');
        if (fcpEntry) {
          setWebVitals(prev => ({ ...prev, FCP: Math.round(fcpEntry.startTime) }));
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
      observers.push(fcpObserver);

      // CLS 관찰
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
            setWebVitals(prev => ({ ...prev, CLS: Math.round(clsValue * 1000) / 1000 }));
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObserver);

    } catch (e) {
      console.debug('PerformanceObserver not fully supported');
    }

    // Navigation timing으로 TTFB 가져오기
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      setWebVitals(prev => ({ ...prev, TTFB: Math.round(navEntry.responseStart) }));
    }

    return () => {
      observers.forEach(obs => obs.disconnect());
    };
  }, []);

  useEffect(() => {
    if (isInitialized && IMQA) {
      try {
        const id = IMQA.getSessionId();
        setSessionId(id);
        updateResourceAttributes();

        // 주기적으로 resourceAttributes 업데이트 (1초마다)
        intervalRef.current = setInterval(updateResourceAttributes, 1000);
      } catch (error) {
        console.error('Failed to get session ID:', error);
        setSessionId(null);
      }
    } else {
      setSessionId(null);
      setResourceAttributes(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isInitialized, IMQA, updateResourceAttributes]);

  return (
    <div className="space-y-4 overflow-hidden">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">Status:</span>
        <Badge variant={isInitialized ? "default" : "outline"}>
          {isInitialized ? "Active" : "Inactive"}
        </Badge>
      </div>

      {isInitialized && (
        <>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Session ID:</span>
            <span className="text-xs font-mono bg-muted p-1 rounded">
              {sessionId ? sessionId.substring(0, 8) + '...' : 'Unknown'}
            </span>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">Active Features:</h4>
            <div className="flex flex-wrap gap-1">
              {config.consoleCapture && (
                <Badge variant="secondary" className="text-xs">Console Capture</Badge>
              )}
              {config.advancedNetworkCapture && (
                <Badge variant="secondary" className="text-xs">Advanced Network</Badge>
              )}
              {config.debug && (
                <Badge variant="secondary" className="text-xs">Debug</Badge>
              )}
              {!config.disableReplay && (
                <Badge variant="secondary" className="text-xs">Session Replay</Badge>
              )}
            </div>
          </div>

          {/* Web Vitals 섹션 */}
          {Object.keys(webVitals).length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <h4 className="text-sm font-medium">Web Vitals</h4>
              <div className="grid grid-cols-2 gap-2">
                {webVitals.LCP !== undefined && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">LCP:</span>
                    <Badge variant={webVitals.LCP <= 2500 ? "default" : webVitals.LCP <= 4000 ? "secondary" : "destructive"} className="text-xs">
                      {webVitals.LCP}ms
                    </Badge>
                  </div>
                )}
                {webVitals.FCP !== undefined && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">FCP:</span>
                    <Badge variant={webVitals.FCP <= 1800 ? "default" : webVitals.FCP <= 3000 ? "secondary" : "destructive"} className="text-xs">
                      {webVitals.FCP}ms
                    </Badge>
                  </div>
                )}
                {webVitals.CLS !== undefined && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">CLS:</span>
                    <Badge variant={webVitals.CLS <= 0.1 ? "default" : webVitals.CLS <= 0.25 ? "secondary" : "destructive"} className="text-xs">
                      {webVitals.CLS}
                    </Badge>
                  </div>
                )}
                {webVitals.TTFB !== undefined && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">TTFB:</span>
                    <Badge variant={webVitals.TTFB <= 800 ? "default" : webVitals.TTFB <= 1800 ? "secondary" : "destructive"} className="text-xs">
                      {webVitals.TTFB}ms
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resource Attributes 섹션 */}
          <div className="space-y-2 pt-2 border-t">
            <button
              onClick={() => setShowAttributes(!showAttributes)}
              className="flex items-center justify-between w-full text-sm font-medium hover:text-primary transition-colors"
            >
              <span>Resource Attributes</span>
              <span className="text-xs text-muted-foreground">
                {showAttributes ? '▼' : '▶'} {lastUpdated && <span className="ml-1">({lastUpdated.toLocaleTimeString()})</span>}
              </span>
            </button>
            {showAttributes && resourceAttributes && (
              <JsonHighlight data={resourceAttributes} />
            )}
            {showAttributes && !resourceAttributes && (
              <p className="text-xs text-muted-foreground">No resource attributes available</p>
            )}
          </div>
        </>
      )}

      {!isInitialized && (
        <p className="text-sm text-muted-foreground">
          IMQA Web Agent is not running. Configure and activate it to start monitoring.
        </p>
      )}
    </div>
  );
}